import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { DetailPanel } from '../components/DetailPanel';
import { ImagePreviewModal } from '../components/ImagePreviewModal';

interface ContentItem {
  id: string;
  userId: string;
  theme: string;
  imageUrl: string;
  blobPath: string;
  blobContainer: string;
  isActive: boolean;
  isServed: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  placement?: string;
  rank_enabled?: boolean;
  rank_number?: number;
  status?: 'draft' | 'ready' | 'rejected' | 'served';
  contentType?: 'reward_card' | 'leaderboard' | 'festival';
  priority?: number;
  generationMetadata?: any;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPlacement: Record<string, number>;
}

export function ContentManager() {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [filteredContent, setFilteredContent] = useState<ContentItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [previewImage, setPreviewImage] = useState<ContentItem | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [placementFilter, setPlacementFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Sorting
  const [sortBy, setSortBy] = useState<'userId' | 'theme' | 'status' | 'placement' | 'createdAt'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  // Load content
  const loadContent = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch more items than needed for client-side filtering
      // This is a compromise until we implement server-side filtering
      const data: any = await api.getAllContent({ limit: 500 });
      setContent(data.items || []);
      setStats(data.stats || null);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...content];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.contentType === typeFilter);
    }

    // Placement filter
    if (placementFilter !== 'all') {
      filtered = filtered.filter(item => item.placement === placementFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.userId.toLowerCase().includes(query) ||
        item.theme.toLowerCase().includes(query) ||
        item.id.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'userId':
          aValue = a.userId.toLowerCase();
          bValue = b.userId.toLowerCase();
          break;
        case 'theme':
          aValue = a.theme.toLowerCase();
          bValue = b.theme.toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'placement':
          aValue = (a.placement || '').toLowerCase();
          bValue = (b.placement || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredContent(filtered);
    setTotalItems(filtered.length);
    
    // Reset to page 1 when filters change
    if (currentPage > Math.ceil(filtered.length / pageSize)) {
      setCurrentPage(1);
    }
  }, [content, statusFilter, typeFilter, placementFilter, searchQuery, sortBy, sortDirection, currentPage, pageSize]);

  // Handle selection
  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedItems(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === paginatedContent.length && paginatedContent.length > 0) {
      // Deselect all on current page
      const newSelection = new Set(selectedItems);
      paginatedContent.forEach(item => newSelection.delete(item.id));
      setSelectedItems(newSelection);
    } else {
      // Select all on current page
      const newSelection = new Set(selectedItems);
      paginatedContent.forEach(item => newSelection.add(item.id));
      setSelectedItems(newSelection);
    }
  };

  // Bulk operations
  const handleBulkUpdate = async (updates: any) => {
    try {
      const items = Array.from(selectedItems).map(id => {
        const item = content.find(c => c.id === id);
        return { id, userId: item!.userId };
      });

      await api.bulkUpdateContent(items, updates);
      setSelectedItems(new Set());
      await loadContent(true);
    } catch (error) {
      console.error('Bulk update failed:', error);
      alert('Failed to update items. Please try again.');
    }
  };

  // Handle content update from detail panel
  const handleContentUpdate = async () => {
    await loadContent(true);
    if (selectedContent) {
      // Reload the selected content
      const updated = content.find(c => c.id === selectedContent.id);
      if (updated) {
        setSelectedContent(updated);
      }
    }
  };

  // Handle sorting
  const handleSort = (column: 'userId' | 'theme' | 'status' | 'placement' | 'createdAt') => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Get sort indicator
  const getSortIndicator = (column: string) => {
    if (sortBy !== column) return ' ↕️';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getStatusBadge = (status?: string) => {
    const colors = {
      draft: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      served: 'bg-blue-100 text-blue-800',
    };
    const color = colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${color}`}>
        {status || 'unknown'}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 120) return '1 minute ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    return `${Math.floor(seconds / 3600)} hours ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-linepe-orange mx-auto mb-4"></div>
          <p className="text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Manager 🎨</h1>
          <p className="text-gray-600 mt-1">View and manage all generated content</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Last updated: {getTimeSince(lastUpdate)}
          </div>
          <button
            onClick={() => loadContent(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 font-medium"
          >
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Total Items</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Draft</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.byStatus.draft || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Ready</p>
            <p className="text-2xl font-bold text-green-600">{stats.byStatus.ready || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Served</p>
            <p className="text-2xl font-bold text-blue-600">{stats.byStatus.served || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
              <option value="rejected">Rejected</option>
              <option value="served">Served</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
            >
              <option value="all">All Types</option>
              <option value="reward_card">Reward Card</option>
              <option value="leaderboard">Leaderboard</option>
              <option value="festival">Festival</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placement</label>
            <select
              value={placementFilter}
              onChange={(e) => setPlacementFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
            >
              <option value="all">All Placements</option>
              <option value="feed">Feed</option>
              <option value="banner">Banner</option>
              <option value="profile">Profile</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="User ID, theme..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linepe-orange"
            />
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedItems.size > 0 && (
        <div className="bg-linepe-orange text-white p-4 rounded-lg shadow mb-4 flex items-center justify-between">
          <div className="font-medium">
            {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkUpdate({ status: 'ready' })}
              className="px-4 py-2 bg-white text-linepe-orange rounded-lg hover:bg-gray-100 transition-colors"
            >
              Mark Ready
            </button>
            <button
              onClick={() => handleBulkUpdate({ isActive: true })}
              className="px-4 py-2 bg-white text-linepe-orange rounded-lg hover:bg-gray-100 transition-colors"
            >
              Set Active
            </button>
            <button
              onClick={() => handleBulkUpdate({ isActive: false })}
              className="px-4 py-2 bg-white text-linepe-orange rounded-lg hover:bg-gray-100 transition-colors"
            >
              Set Inactive
            </button>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Content Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={paginatedContent.length > 0 && paginatedContent.every(item => selectedItems.has(item.id))}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-linepe-orange focus:ring-linepe-orange"
                    title="Select all on this page"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preview
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('userId')}
                  title="Click to sort"
                >
                  User ID{getSortIndicator('userId')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('theme')}
                  title="Click to sort"
                >
                  Theme{getSortIndicator('theme')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('status')}
                  title="Click to sort"
                >
                  Status{getSortIndicator('status')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('placement')}
                  title="Click to sort"
                >
                  Placement{getSortIndicator('placement')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  onClick={() => handleSort('createdAt')}
                  title="Click to sort"
                >
                  Created{getSortIndicator('createdAt')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedContent.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No content found matching your filters
                  </td>
                </tr>
              ) : (
                paginatedContent.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => toggleSelection(item.id)}
                        className="rounded border-gray-300 text-linepe-orange focus:ring-linepe-orange"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <img
                        src={item.imageUrl}
                        alt={item.theme}
                        onClick={() => setPreviewImage(item)}
                        className="w-16 h-20 object-cover rounded border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                        loading="lazy"
                        title="Click to preview"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {item.userId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="max-w-xs truncate">{item.theme}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getStatusBadge(item.status)}
                      {item.isServed && (
                        <span className="ml-1 text-xs text-blue-600">• Served</span>
                      )}
                      {item.isActive && (
                        <span className="ml-1 text-xs text-green-600">• Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{item.placement || 'N/A'}</div>
                      {item.rank_enabled && (
                        <div className="text-xs text-gray-500">Rank #{item.rank_number}</div>
                      )}
                      {item.priority !== undefined && (
                        <div className="text-xs text-gray-500">Priority: {item.priority}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => setSelectedContent(item)}
                        className="text-linepe-orange hover:text-linepe-orange-dark font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            {/* Left: Items info and page size selector */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{totalItems}</span> items
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700">Items per page:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-linepe-orange"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Right: Page navigation */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <div className="flex gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-3 py-1 text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page as number)}
                        className={`px-3 py-1 border rounded text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-linepe-orange text-white border-linepe-orange'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedContent && (
        <DetailPanel
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
          onUpdate={handleContentUpdate}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          imageUrl={previewImage.imageUrl}
          title={`${previewImage.theme} - User ${previewImage.userId}`}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
}

